<template>
    <div class="workforce-plan-display-controls">
        <div class="plan-display-control">
            <div class="plan-display-control__caption">
                <label for="plan-zoom-slider">Масштаб <span>{{ zoom === 'fit' ? fitLabel : `${zoom}%` }}</span></label>
                <b-button size="sm" :variant="zoom === 'fit' ? 'primary' : 'outline-secondary'"
                          class="plan-display-control__fit" :aria-pressed="zoom === 'fit' ? 'true' : 'false'"
                          :title="fitTitle" @click="$emit('fit')">{{ fitLabel }}</b-button>
            </div>
            <input id="plan-zoom-slider" type="range" min="50" max="125" step="5"
                   :value="zoom === 'fit' ? numericZoom : zoom" :style="trackStyle(zoom === 'fit' ? numericZoom : zoom, 50, 125)"
                   :aria-valuetext="zoom === 'fit' ? fitLabel : `${zoom}%`"
                   @input="$emit('zoom', Number($event.target.value))">
        </div>
        <div id="plan-contrast-control" class="plan-display-control">
            <div class="plan-display-control__caption">
                <label for="plan-contrast-slider">Контрастность <span>{{ contrast }}%</span></label>
            </div>
            <input id="plan-contrast-slider" type="range" min="0" max="100" step="5"
                   :value="contrast" :style="trackStyle(contrast, 0, 100)" :aria-valuetext="`${contrast}%`"
                   @input="changeContrast(Number($event.target.value))">
        </div>
        <b-popover target="plan-contrast-control" :show="hintVisible" triggers="manual" placement="bottom"
                   boundary="viewport" custom-class="workforce-plan-contrast-hint">
            <template #title>Новое: контрастность плана</template>
            <p>Ячейки сливаются? Подвиньте ползунок, чтобы сделать цвета насыщеннее.
                Настройка сохранится на этом устройстве.</p>
            <b-button size="sm" variant="primary" @click="dismissHint">Понятно</b-button>
        </b-popover>
    </div>
</template>

<script>
import display from '@/utils/employeeWorkforcePlanDisplay';

export default {
    name: 'EmployeeWorkforceDisplayControls',
    props: {
        fitLabel: {type: String, default: 'Весь год'},
        fitTitle: {type: String, default: 'Уместить год по ширине'},
        zoom: {type: [Number, String], default: 90},
        numericZoom: {type: Number, default: 90},
        contrast: {type: Number, default: 0},
        ready: {type: Boolean, default: false},
        visible: {type: Boolean, default: true},
        blocked: {type: Boolean, default: false},
        user: {type: Object, default: null}
    },
    data() { return {active: true, hintRestored: false, dismissedKeys: [], modalIds: []}; },
    computed: {
        hintKey() { return display.contrastHintKey(this.user); },
        hintVisible() {
            return this.hintRestored && this.active && this.ready && this.visible && !this.blocked
                && !this.modalIds.length && !this.dismissedKeys.includes(this.hintKey);
        }
    },
    watch: {hintKey() { this.restoreHint(); }},
    mounted() {
        this.restoreHint();
        document.addEventListener('keydown', this.handleEscape, true);
        this.$root.$on('bv::modal::show', this.modalShow);
        this.$root.$on('bv::modal::hidden', this.modalHidden);
    },
    activated() { this.active = true; this.restoreHint(); },
    deactivated() { this.active = false; },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscape, true);
        this.$root.$off('bv::modal::show', this.modalShow);
        this.$root.$off('bv::modal::hidden', this.modalHidden);
    },
    methods: {
        trackStyle(value, min, max) { return {'--range-fill': `${(value - min) / (max - min) * 100}%`}; },
        restoreHint() {
            if (display.readLocal(this.hintKey) === 'dismissed' && !this.dismissedKeys.includes(this.hintKey)) {
                this.dismissedKeys.push(this.hintKey);
            }
            this.hintRestored = true;
        },
        dismissHint() {
            if (!this.dismissedKeys.includes(this.hintKey)) this.dismissedKeys.push(this.hintKey);
            display.writeLocal(this.hintKey, 'dismissed');
        },
        changeContrast(value) { this.dismissHint(); this.$emit('contrast', value); },
        handleEscape(event) {
            if (event.key === 'Escape' && this.hintVisible) {
                event.preventDefault();
                event.stopPropagation();
                this.dismissHint();
            }
        },
        modalShow(event, id) {
            this.$nextTick(() => {
                if (!event.defaultPrevented && !this.modalIds.includes(id)) this.modalIds.push(id);
            });
        },
        modalHidden(event, id) { this.modalIds = this.modalIds.filter(value => value !== id); }
    }
};
</script>

<style scoped>
.workforce-plan-display-controls { display: flex; align-items: center; gap: 18px; min-width: 0; }
.plan-display-control { flex: 1 1 165px; width: 180px; min-width: 140px; }
.plan-display-control__caption { display: flex; align-items: center; justify-content: space-between; min-height: 22px; gap: 6px; }
.plan-display-control label { margin: 0; color: #52677a; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }
.plan-display-control label span { margin-left: 5px; color: #30485d; font-variant-numeric: tabular-nums; }
.plan-display-control__fit { padding: 1px 5px; font-size: 0.63rem; white-space: nowrap; }
.plan-display-control input[type="range"] {
    display: block; width: 100%; height: 24px; margin: 0; padding: 0; cursor: pointer;
    appearance: none; -webkit-appearance: none; background: transparent;
}
.plan-display-control input[type="range"]::-webkit-slider-runnable-track {
    height: 4px; border-radius: 4px; background: linear-gradient(to right, #1976bd var(--range-fill), #dce5ed var(--range-fill));
}
.plan-display-control input[type="range"]::-moz-range-track { height: 4px; border-radius: 4px; background: #dce5ed; }
.plan-display-control input[type="range"]::-moz-range-progress { height: 4px; border-radius: 4px; background: #1976bd; }
.plan-display-control input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none; width: 14px; height: 14px; margin-top: -5px; border: 2px solid #1976bd;
    border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(36, 57, 76, 0.16);
}
.plan-display-control input[type="range"]::-moz-range-thumb { width: 10px; height: 10px; border: 2px solid #1976bd; border-radius: 50%; background: #fff; }
.plan-display-control input[type="range"]:focus-visible { outline: 2px solid #1976bd; outline-offset: 2px; border-radius: 4px; }
@media (max-width: 767.98px) {
    .workforce-plan-display-controls { flex-direction: column; align-items: stretch; gap: 6px; }
    .plan-display-control { flex: 0 0 auto; width: 100%; }
    .plan-display-control input[type="range"] { height: 32px; }
    .plan-display-control input[type="range"]::-webkit-slider-thumb { width: 18px; height: 18px; margin-top: -7px; }
    .plan-display-control input[type="range"]::-moz-range-thumb { width: 14px; height: 14px; }
}
</style>

<style>
.workforce-plan-contrast-hint { max-width: min(320px, calc(100vw - 24px)); }
.workforce-plan-contrast-hint .popover-body { color: #3f5367; font-size: 0.78rem; }
.workforce-plan-contrast-hint .popover-body p { margin-bottom: 9px; }
</style>
